import {inject, observer} from "mobx-react";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';

import ReceiptTotals, {expenseLineAmountLabel} from './receipt-totals';

import {formatMoney} from '../../utils/format-money';
import {formatLineNumbers} from '../../utils/line-number';

const formatUserName = (user) => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    return fullName || user?.username || '';
};

const formatRequestedAt = (value) => {
    if (!value) {
        return '';
    }

    const isNumericTimestamp = typeof value === 'number' || /^\d+$/.test(String(value));
    const timestamp = isNumericTimestamp ? Number(value) : value;
    const requestedAt = new Date(timestamp);
    if (Number.isNaN(requestedAt.getTime())) {
        return value;
    }

    return requestedAt.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const translate = (data, key) => data?.t?.read?.(key) || key;

const getResolvedActionClassName = (action) => {
    if (action === 'approved') {
        return 'text-success font-weight-bold';
    }
    if (action === 'denied') {
        return 'text-danger font-weight-bold';
    }
    return 'font-weight-bold';
};

const requestColumns = [
    {
        id: 'type',
        label: 'approval_requests_table.type',
        // XPEN-3790: a line request is named by its number in the report, as every screen shows it.
        format: (value, data, row) => value === 'line_item'
            ? (row?.line_number ? formatLineNumbers([row.line_number], data?.t) : translate(data, 'approval_requests_table.line_item'))
            : translate(data, 'approval_requests_table.invoice'),
    },
    {id: 'supplier_name', label: 'approval_requests_table.supplier'}
    ,
    {id: 'invoice_number', label: 'approval_requests_table.invoice_number'},
    {
        id: 'requester',
        label: 'approval_requests_table.requester',
        format: formatUserName,
    },
    {
        id: 'assigned_approver',
        label: 'approval_requests_table.assigned_approver',
        format: formatUserName,
    },
];

const pendingColumns = [
    {
        id: 'requested_at',
        label: 'approval_requests_table.requested',
        format: formatRequestedAt,
    },
];

const historyColumns = [
    {
        id: 'resolved_action',
        label: 'approval_requests_table.status',
        format: (value, data) => (
            <span className={getResolvedActionClassName(value)}>
                {translate(data, `status.${value}`)}
            </span>
        ),
    },
    {
        id: 'resolved_at',
        label: 'approval_requests_table.resolved',
        format: formatRequestedAt,
    },
];

const amountColumn = [
    {
        id: 'total',
        label: 'approval_requests_table.amount',
        align: 'right',
        getValue: (row) => row.type === 'line_item' ? row.line_item_total : row.invoice_total,
        format: (value) => formatMoney(value)
    }
];

function ApprovalRequestTable({
    data,
    rows = [],
    mode = 'pending',
    onRowClick = () => {},
    selectedRowIds = [],
    onRowSelectionChange = () => {},
    onSelectAllRows = () => {},
}) {
    // Keep each receipt's visible approval lines together without merging actions.
    const groups = new Map();
    rows.forEach((row) => {
        const key = row.type === 'line_item' && row.receipts?.length === 1
            ? `receipt:${row.receipts[0].id}` : `request:${row.id}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
    });
    const groupedRows = [...groups.values()].flat();
    const seenReceipts = new Set();
    const rowReceipts = new Map(groupedRows.map((row) => [row.id, (row.receipts || []).filter((receipt) => {
        if (seenReceipts.has(receipt.id)) return false;
        seenReceipts.add(receipt.id);
        return true;
    })]));
    const hasExpenseRows = rows.some((row) => row.invoice_type === 'XPENSE');
    const isResolvedMode = mode === 'history' || mode === 'denied_requests' || mode === 'approved_requests';
    const columns = [
        ...requestColumns,
        ...(isResolvedMode ? historyColumns : pendingColumns),
        ...amountColumn,
        ...(hasExpenseRows ? [{id: 'receipt_total', label: 'approval_requests_table.receipt_total', align: 'right'}] : []),
    ];
    const canSelectRows = mode === 'pending';
    const selectedIds = new Set(selectedRowIds);
    const selectedLoadedCount = rows.filter((row) => selectedIds.has(row.id)).length;
    const allLoadedSelected = rows.length > 0 && selectedLoadedCount === rows.length;
    const partiallySelected = selectedLoadedCount > 0 && !allLoadedSelected;

    return (
        <Paper
            elevation={0}
            className={"mt-4"}
            sx={{width: '100%', overflow: 'hidden', border: '1px solid #c1c7d0'}}
        >
            <TableContainer sx={{maxHeight: 440}}>
                <Table
                    stickyHeader
                    aria-label="approval requests table"
                    sx={{
                        '& .MuiTableCell-root': {
                            fontSize: '1rem',
                        },
                    }}
                >
                    <TableHead color={"primary"}>
                        <TableRow>
                            {canSelectRows && (
                                <TableCell
                                    padding="checkbox"
                                    sx={{backgroundColor: '#f4f5f7'}}
                                >
                                    <Checkbox
                                        color="primary"
                                        checked={allLoadedSelected}
                                        indeterminate={partiallySelected}
                                        inputProps={{'aria-label': translate(data, 'approval_requests_table.select_all')}}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={(event) => onSelectAllRows(event.target.checked)}
                                    />
                                </TableCell>
                            )}
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                        minWidth: column.minWidth,
                                        backgroundColor: '#f4f5f7',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                    }}
                                >
                                    {translate(data, column.label)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupedRows.map((row) => (
                            <TableRow
                                hover
                                tabIndex={0}
                                key={row.id}
                                onClick={() => onRowClick(row)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onRowClick(row);
                                    }
                                }}
                                sx={{cursor: 'pointer'}}
                            >
                                {canSelectRows && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={selectedIds.has(row.id)}
                                            inputProps={{'aria-label': translate(data, 'approval_requests_table.select_request')}}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={(event) => onRowSelectionChange(row.id, event.target.checked)}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column) => {
                                    if (column.id === 'receipt_total') {
                                        const receipts = rowReceipts.get(row.id);
                                        return <TableCell key={column.id} align="right">
                                            {row.invoice_type === 'XPENSE' && (receipts.length > 0 || !row.receipts?.length) && (
                                                <ReceiptTotals data={data} receipts={receipts} />
                                            )}
                                        </TableCell>;
                                    }
                                    const value = column.getValue ? column.getValue(row) : row[column.id];
                                    return (
                                        <TableCell key={column.id} align={column.align}>
                                            {column.format ? column.format(value, data, row) : value}
                                            {column.id === 'total' && row.type === 'line_item' && row.invoice_type === 'XPENSE' && (
                                                <div className="text-muted small">{expenseLineAmountLabel(data)}</div>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default inject('data')(observer(ApprovalRequestTable));
